import { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Button,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  EmojiEvents as TrophyIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useStore from '../config/store';

const CvFilteringResults = ({ filtering, onBack }) => {
  const { t } = useTranslation();
  const { showAlert } = useStore();
  const [expandedPanel, setExpandedPanel] = useState(null);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : null);
  };

  const sortedResults = [...(filtering.results || [])]
    .sort((a, b) => (b.score || 0) - (a.score || 0))
    .map((result, index) => ({ ...result, rank: index + 1 }));

  const getRankIcon = (rank) => {
    if (rank === 1) return <TrophyIcon sx={{ color: 'gold', mr: 1 }} />;
    if (rank === 2) return <TrophyIcon sx={{ color: 'silver', mr: 1 }} />;
    if (rank === 3) return <TrophyIcon sx={{ color: '#cd7f32', mr: 1 }} />;
    return null;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const parseAiAnalysis = (aiAnalysisString) => {
    try {
      return JSON.parse(aiAnalysisString);
    } catch {
      return {};
    }
  };

  const averageScore = sortedResults.length > 0
    ? (sortedResults.reduce((acc, r) => acc + (r.score || 0), 0) / sortedResults.length).toFixed(1)
    : '0';

  const weightCV = filtering.weightCV ?? 50;
  const weightRespuestas = filtering.weightRespuestas ?? 50;

  const handleDownloadPdf = async () => {
    try {
      const response = await fetch(filtering.reportUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filtering.title || 'cv-filtering'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      showAlert(t('cvFiltering.downloadError'), 'error');
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={onBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5">{filtering.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('cvFiltering.position')}: {filtering.positionName}
            </Typography>
          </Box>
        </Box>

        {filtering.reportUrl && (
          <Button
            variant="outlined"
            startIcon={<PdfIcon />}
            onClick={handleDownloadPdf}
          >
            {t('cvFiltering.downloadPdf')}
          </Button>
        )}
      </Box>

      {/* Resumen */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t('cvFiltering.totalApplicants')}
            </Typography>
            <Typography variant="h5">{filtering.applicants?.length || 0}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t('cvFiltering.processed')}
            </Typography>
            <Typography variant="h5">{sortedResults.length}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t('cvFiltering.averageScore')}
            </Typography>
            <Typography variant="h5">{averageScore}</Typography>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('cvFiltering.scoringWeights')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                label={`${t('cvFiltering.cv')} ${weightCV}%`}
                size="small"
                sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600, border: '1px solid #bbdefb' }}
              />
              <Chip
                label={`${t('cvFiltering.answers')} ${weightRespuestas}%`}
                size="small"
                sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600, border: '1px solid #bbdefb' }}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Ranking de candidatos con accordions */}
      <Typography variant="h6" gutterBottom>
        {t('cvFiltering.candidateRanking')}
      </Typography>

      {sortedResults.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('cvFiltering.noResults')}
        </Typography>
      ) : (
        sortedResults.map((result) => {
          const analysis = parseAiAnalysis(result.aiAnalysis);

          return (
            <Accordion
              key={result.applicantId}
              expanded={expandedPanel === result.applicantId}
              onChange={handleAccordionChange(result.applicantId)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                  {/* Ranking */}
                  <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 60 }}>
                    {getRankIcon(result.rank)}
                    <Typography variant="h6">#{result.rank}</Typography>
                  </Box>

                  {/* Nombre */}
                  <Typography variant="body1" sx={{ flexGrow: 1 }}>
                    {result.applicantName}
                  </Typography>

                  {/* Score */}
                  <Chip
                    label={`${result.score?.toFixed(1) || 0}%`}
                    color={getScoreColor(result.score || 0)}
                    size="small"
                  />
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                {/* Barra de progreso */}
                <Box sx={{ mb: 3 }}>
                  <LinearProgress
                    variant="determinate"
                    value={result.score || 0}
                    sx={{ height: 10, borderRadius: 1 }}
                    color={getScoreColor(result.score || 0)}
                  />
                </Box>

                {/* Scores desglosados con ponderación */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    {t('cvFiltering.detailedScores')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Chip
                      label={`${t('cvFiltering.cv')}: ${result.scoreCV?.toFixed(1) || 0}% (${t('cvFiltering.weight')} ${weightCV}%)`}
                      variant="outlined"
                      size="small"
                    />
                    <Chip
                      label={`${t('cvFiltering.answers')}: ${result.scoreRespuestas?.toFixed(1) || 0}% (${t('cvFiltering.weight')} ${weightRespuestas}%)`}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Fortalezas */}
                {result.strengths && result.strengths.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="success.main">
                      {t('cvFiltering.strengths')}
                    </Typography>
                    <List dense>
                      {result.strengths.map((strength, idx) => (
                        <ListItem key={idx} sx={{ py: 0 }}>
                          <ListItemText primary={`• ${strength}`} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Debilidades */}
                {result.weaknesses && result.weaknesses.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="error.main">
                      {t('cvFiltering.areasForImprovement')}
                    </Typography>
                    <List dense>
                      {result.weaknesses.map((weakness, idx) => (
                        <ListItem key={idx} sx={{ py: 0 }}>
                          <ListItemText primary={`• ${weakness}`} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Análisis del CV */}
                {analysis.analisisCV && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      {t('cvFiltering.cvAnalysis')}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {analysis.analisisCV}
                    </Typography>
                  </Box>
                )}

                {/* Análisis de Respuestas */}
                {analysis.analisisRespuestas && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      {t('cvFiltering.answersAnalysis')}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {analysis.analisisRespuestas}
                    </Typography>
                  </Box>
                )}

                {/* Justificación del Score */}
                {analysis.justificacionScoreFinal && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      {t('cvFiltering.scoreJustification')}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {analysis.justificacionScoreFinal}
                    </Typography>
                  </Box>
                )}

                {/* Observaciones */}
                {analysis.observaciones && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      {t('cvFiltering.observations')}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {analysis.observaciones}
                    </Typography>
                  </Box>
                )}

                {/* Recomendación */}
                {result.recommendation && (
                  <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold" sx={{ color: 'primary.contrastText' }}>
                      {t('cvFiltering.finalRecommendation')}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'primary.contrastText' }}>
                      {result.recommendation}
                    </Typography>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })
      )}
    </Box>
  );
};

export default CvFilteringResults;